import { createSlice } from '@reduxjs/toolkit';

import ArcadaApiService from 'helpers/service/arcadaApiService';
import { FormatErrorResponse, FormatSuccessResponse } from 'utils/formatErrorResponse';
import { getFormData } from 'utils/formData';
import { TABLE_ROWS_COUNT_OPTIONS_MAX } from 'utils/table';

import { dispatch } from '../store';

import { slice as globalNotificationSlice } from './globalNotifications';

const initialState = {
  isLoading: false,
  partnerOffers: null,
  partnerOffer: null,
  error: null,
  attachmentFiles: null,
};

const slice = createSlice({
  name: 'partnerOffers',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    getEntitiesSuccess(state, action) {
      state.isLoading = false;
      state.partnerOffers = action.payload;
    },
    getEntitySuccess(state, action) {
      state.isLoading = false;
      state.partnerOffer = action.payload;
    },
    getEntityAttachmentsSuccess(state, action) {
      state.isLoading = false;
      state.attachmentFiles = action.payload;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    hasSuccess(state) {
      state.isLoading = false;
      state.error = false;
    },
    resetEntity(state) {
      state.partnerOffer = null;
      state.attachmentFiles = null;
      state.error = null;
    },
    addAttachments(state, action) {
      state.attachmentFiles = [...(state.attachmentFiles || []), ...action.payload];
    },
    deleteAttachments(state, action) {
      state.attachmentFiles = action.payload;
    },
  },
});

export default slice.reducer;

export const { addAttachments, deleteAttachments } = slice.actions;

export function getPartnerOffers(
  skip = 0,
  take = TABLE_ROWS_COUNT_OPTIONS_MAX[0],
  ordering = 'order',
  search,
  filters
) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const { data } = await ArcadaApiService.partnerOffers.getPartnerOffers({
        skip,
        take,
        ordering,
        search,
        ...filters,
      });
      dispatch(slice.actions.getEntitiesSuccess(data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      dispatch(globalNotificationSlice.actions.hasNotification(FormatErrorResponse(error)));
    }
  };
}

export function getPartnerOffer(id) {
  return async () => {
    dispatch(slice.actions.startLoading());

    try {
      const { data } = await ArcadaApiService.partnerOffers.getPartnerOffer(id);
      dispatch(slice.actions.getEntitySuccess(data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      dispatch(globalNotificationSlice.actions.hasNotification(FormatErrorResponse(error)));
    }
  };
}

export function addPartnerOffer(data, attachments) {
  return async () => {
    try {
      dispatch(slice.actions.startLoading());
      const formData = getFormData(data);

      const { data: result } = await ArcadaApiService.partnerOffers.addPartnerOffer(formData);

      if (attachments?.length) {
        attachments.forEach((file) => {
          dispatch(
            addPartnerOfferAttachments(
              getFormData({
                file,
                partner_offer: result?.id,
              })
            )
          );
        });
      }

      dispatch(slice.actions.hasSuccess());
      dispatch(slice.actions.getEntitySuccess(data));
      dispatch(globalNotificationSlice.actions.hasNotification(FormatSuccessResponse(['Խանութն ավելացված է'])));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      dispatch(globalNotificationSlice.actions.hasNotification(FormatErrorResponse(error)));
    }
  };
}

export function editPartnerOffer(dirtyData, id, attachments) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      // eslint-disable-next-line no-unused-vars
      const { attachmentDeleteIds, attachmentFiles, ...rest } = dirtyData || {};

      if (Object.keys(rest).length) {
        const formData = getFormData({ ...rest });
        await ArcadaApiService.partnerOffers.editPartnerOffer(formData, id);
      }

      if (attachmentDeleteIds?.length)
        attachmentDeleteIds?.filter((id) => ArcadaApiService.partnerOffers.deletePartnerOfferAttachment(id));

      if (attachments?.length) {
        attachments.forEach((file) => {
          if (typeof file !== 'object' || file?.id) return;
          dispatch(
            addPartnerOfferAttachments(
              getFormData({
                file,
                partner_offer: id,
              })
            )
          );
        });
      }

      dispatch(slice.actions.hasSuccess());
      dispatch(globalNotificationSlice.actions.hasNotification(FormatSuccessResponse(['Խանութը թարմացված է'])));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      dispatch(globalNotificationSlice.actions.hasNotification(FormatErrorResponse(error)));
    }
  };
}

export function togglePartnerOfferStatus(
  id,
  isActive,
  skip = 0,
  take = TABLE_ROWS_COUNT_OPTIONS_MAX[0],
  ordering = '-name',
  search,
  filters
) {
  const formData = getFormData({ is_active: isActive });

  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await ArcadaApiService.partnerOffers.editPartnerOffer(formData, id);
      dispatch(getPartnerOffers(skip, take, ordering, search, filters));
    } catch (error) {
      dispatch(globalNotificationSlice.actions.hasNotification(FormatErrorResponse(error)));
    }
  };
}

export function getPartnerOfferAttachments(id) {
  return async () => {
    // dispatch(slice.actions.startLoading());
    try {
      const { data } = await ArcadaApiService.partnerOffers.getPartnerOfferAttachments({ partner_offer: id });
      dispatch(slice.actions.getEntityAttachmentsSuccess(data?.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      dispatch(globalNotificationSlice.actions.hasNotification(FormatErrorResponse(error)));
    }
  };
}

export function addPartnerOfferAttachments(data) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await ArcadaApiService.partnerOffers.addPartnerOfferAttachments(data);
      dispatch(slice.actions.hasSuccess());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      dispatch(
        globalNotificationSlice.actions.hasNotification(FormatErrorResponse({ message: ['Ինչ որ բան սխալ է'] }))
      );
    }
  };
}

export function resetPartnerOffer() {
  return () => {
    dispatch(slice.actions.resetEntity());
  };
}
